import { useMemo } from "react";
import { IconSearch } from "@tabler/icons-react";
import {
  BotonExportarExcel,
  Card,
  EmptyState,
  Group,
  Input,
  PageHeader,
  PaginadorTabla,
  Select,
  Skeleton,
  Stack,
  TablaSkeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import { useServerPagination } from "@/lib/hooks/use-server-pagination";
import {
  listarSolicitudesCatalogo,
  type SolicitudDeCatalogo,
} from "@/features/solicitudes-catalogo/services/solicitudes-catalogo.api";
import {
  useBandejaSolicitudesCatalogo,
  useMisTiposResponsableCatalogo,
  useSolicitudesCatalogoPendientes,
} from "../hooks/use-bandeja-solicitudes-catalogo";
import { FilaSolicitudCatalogo, claveLegible } from "./FilaSolicitudCatalogo";

const FILTROS_ESTADO = ["Pendiente", "Atendida", "Rechazada", "Todas"] as const;

interface Filtros {
  estado: string;
  tipo: string;
  busqueda: string;
}

/** Filtro segmentado (pill) por estado. */
function FiltroSegmentado({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-input bg-muted/40 p-0.5">
      {FILTROS_ESTADO.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={cn(
            "cursor-pointer rounded-sm px-2.5 py-1 text-xs font-medium transition-colors",
            value === opt ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function aParams(f: Filtros) {
  return {
    ...(f.estado !== "Todas" ? { estado: f.estado } : {}),
    ...(f.tipo ? { tipo: f.tipo } : {}),
    ...(f.busqueda.trim() ? { busqueda: f.busqueda.trim() } : {}),
  };
}

/**
 * Bandeja «Solicitudes de catálogo» (spec SPEC_Modulo_SolicitudesDeCatalogo §6) · cada
 * responsable ve SOLO los tipos que atiende (por rol · configurable en
 * /admin/tipos-solicitud-catalogo). Las canceladas por el solicitante no aparecen.
 */
export function BandejaSolicitudesCatalogoPage() {
  const { data: tipos } = useMisTiposResponsableCatalogo();
  const p = useServerPagination<Filtros>({ estado: "Pendiente", tipo: "", busqueda: "" });
  const params = useMemo(
    () => ({ ...aParams(p.filtrosDebounced), page: p.page, pageSize: p.pageSize }),
    [p.filtrosDebounced, p.page, p.pageSize]
  );
  const { data } = useBandejaSolicitudesCatalogo(params);
  const pendientes = useSolicitudesCatalogoPendientes(true).data;
  const visibles = data?.items ?? [];
  const total = data?.total ?? 0;
  const listaTipos = tipos ?? [];

  // Export a Excel · TODAS las páginas con los filtros actuales.
  const obtenerTodas = async (): Promise<SolicitudDeCatalogo[]> => {
    const todas: SolicitudDeCatalogo[] = [];
    for (let pagina = 1; ; pagina++) {
      const r = await listarSolicitudesCatalogo({ ...aParams(p.filtrosDebounced), page: pagina, pageSize: 100 });
      todas.push(...r.items);
      if (todas.length >= r.total || r.items.length === 0) break;
    }
    return todas;
  };

  const nombreTipo = (tipo: string) => listaTipos.find((t) => t.tipo === tipo)?.nombre ?? tipo;

  if (tipos && tipos.length === 0) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <EmptyState
          title="Acceso restringido"
          description="No eres responsable de ningún tipo de solicitud de catálogo."
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <PageHeader
        title="Solicitudes de catálogo"
        description="Peticiones de los solicitantes para dar de alta elementos que no existen en los catálogos."
        breadcrumbs={[{ label: "Inicio", href: "/dashboard" }, { label: "Solicitudes de catálogo" }]}
      />
      <Stack gap="md">
        <Group justify="space-between" className="flex-wrap">
          <Group gap="sm" className="flex-wrap">
            <FiltroSegmentado value={p.filtros.estado} onChange={(estado) => p.patchFiltros({ estado })} />
            {listaTipos.length > 1 && (
              <Select
                size="xs"
                wrapperClassName="w-60"
                value={p.filtros.tipo || "todos"}
                onValueChange={(v) => p.patchFiltros({ tipo: v === "todos" ? "" : v })}
                options={[
                  { value: "todos", label: "Todos los catálogos" },
                  ...listaTipos.map((t) => ({ value: t.tipo, label: t.nombre })),
                ]}
              />
            )}
            <Input
              placeholder="Buscar por clave, descripción o solicitante…"
              leftSection={<IconSearch size={14} stroke={1.7} />}
              value={p.filtros.busqueda}
              onChange={(e) => p.patchFiltros({ busqueda: e.currentTarget.value })}
              size="xs"
              wrapperClassName="w-70"
            />
          </Group>
          <Group gap="sm">
            <Text size="sm" c="dimmed">
              {pendientes === undefined ? (
                <Skeleton height={12} width={170} className="inline-block align-middle" aria-label="Cargando pendientes" />
              ) : pendientes === 0 ? (
                "Sin solicitudes pendientes."
              ) : (
                `${pendientes} solicitud${pendientes === 1 ? "" : "es"} pendiente${pendientes === 1 ? "" : "s"}.`
              )}
            </Text>
            <BotonExportarExcel
              archivo="solicitudes-catalogo"
              hoja="Solicitudes"
              obtenerFilas={obtenerTodas}
              columnas={[
                { header: "Catálogo", value: (s) => nombreTipo(s.tipo) },
                { header: "Clave", value: (s) => claveLegible(s) },
                { header: "Sociedad", value: (s) => s.numeroSociedad },
                { header: "Descripción", value: (s) => s.descripcion },
                { header: "Solicitante", value: (s) => s.solicitanteNombre },
                { header: "Solicitud de pago", value: (s) => s.numeroSolicitudPago },
                { header: "Comentario", value: (s) => s.comentario },
                { header: "Fecha", value: (s) => s.creadoAt },
                { header: "Estado", value: (s) => s.estado },
                { header: "Atendida automáticamente", value: (s) => (s.atendidaAutomaticamente ? "Sí" : "No") },
                { header: "Respuesta", value: (s) => s.respuestaComentario },
                { header: "Resuelta el", value: (s) => s.resueltaAt },
              ]}
            />
          </Group>
        </Group>
        <PaginadorTabla cargando={!data} total={total} page={p.page} pageSize={p.pageSize} onPageChange={p.setPage} variante="superior" />
        <Card padding="lg">
          <div className="overflow-x-auto">
            <Table verticalSpacing="sm" minWidth={860}>
              <Thead>
                <Tr>
                  <Th>Catálogo</Th>
                  <Th>Elemento solicitado</Th>
                  <Th>Solicitante</Th>
                  <Th>Comentario</Th>
                  <Th>Fecha</Th>
                  <Th>Estado</Th>
                  <Th align="center">Acciones</Th>
                </Tr>
              </Thead>
              {!data ? (
                <TablaSkeleton dentroDeTabla columnas={[{}, { lineas: 2 }, {}, {}, {}, {}, { align: "center" }]} filas={p.pageSize} />
              ) : (
                <Tbody>
                  {visibles.length === 0 ? (
                    <Tr>
                      <Td colSpan={7}>
                        <Text size="sm" c="dimmed" ta="center" className="py-5">
                          No hay solicitudes {p.filtros.estado === "Todas" ? "" : p.filtros.estado.toLowerCase() + "s"}.
                        </Text>
                      </Td>
                    </Tr>
                  ) : (
                    visibles.map((s) => <FilaSolicitudCatalogo key={s.id} s={s} tipos={listaTipos} />)
                  )}
                </Tbody>
              )}
            </Table>
          </div>
        </Card>
        <PaginadorTabla cargando={!data} total={total} page={p.page} pageSize={p.pageSize} onPageChange={p.setPage} sustantivo="solicitudes" />
      </Stack>
    </div>
  );
}
